import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import {
  AutoAwesome,
  QuestionMark,
  Calculate,
  Psychology
} from '@mui/icons-material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import InputIcon from '@mui/icons-material/Input';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import DeleteIcon from '@mui/icons-material/Delete';
import CodeIcon from '@mui/icons-material/Code';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NodeOption from '../../../../types/BotBuilder/NodeOption';
import DraggableNode from './DraggableNode/DraggableNode';
import styles from './styles';

const nodeOptions: NodeOption[] = [
  {
    type: 'response',
    label: 'Respond to user',
    icon: <SmartToyIcon color='primary' />,
    isValidChildFor: () => true,
    createNode: () => ({}) as any // Will be implemented in drag handler
  },
  {
    type: 'responseCard',
    label: 'Follow up options',
    icon: <ViewCarouselIcon color='primary' />,
    isValidChildFor: () => true,
    createNode: () => ({}) as any
  },
  {
    type: 'goToIntent',
    label: 'Go to question',
    icon: <QuestionMark color='primary' />,
    isValidChildFor: () => true,
    createNode: () => ({}) as any
  },
  {
    type: 'collectVariable',
    label: 'Collect variable',
    icon: <InputIcon color='primary' />,
    isValidChildFor: () => true,
    createNode: () => ({}) as any
  },
  {
    type: 'conditional',
    label: 'Variable comparison',
    icon: <FilterAltIcon color='primary' />,
    isValidChildFor: () => true,
    createNode: () => ({}) as any
  },
  {
    type: 'generativeAi',
    label: 'AI response',
    icon: <AutoAwesome color='primary' />,
    isValidChildFor: () => true,
    createNode: () => ({}) as any
  },
  {
    type: 'calculation',
    label: 'Calculation',
    icon: <Calculate color='primary' />,
    isValidChildFor: () => true,
    createNode: () => ({}) as any
  },
  {
    type: 'sendEmail',
    label: 'Send email',
    icon: <EmailIcon color='primary' />,
    isValidChildFor: () => true,
    createNode: () => ({}) as any
  },
  {
    type: 'sendSms',
    label: 'Send SMS',
    icon: <SmsIcon color='primary' />,
    isValidChildFor: () => true,
    createNode: () => ({}) as any
  },
  {
    type: 'deleteVariable',
    label: 'Delete variable',
    icon: <DeleteIcon color='primary' />,
    isValidChildFor: () => true,
    createNode: () => ({}) as any
  },
  {
    type: 'codeBlock',
    label: 'Code block',
    icon: <CodeIcon color='primary' />,
    isValidChildFor: () => true,
    createNode: () => ({}) as any
  },
  {
    type: 'llm',
    label: 'LLM prompt',
    icon: <Psychology color='primary' />,
    isValidChildFor: () => true,
    createNode: () => ({}) as any
  },
  {
    type: 'doNothing',
    label: 'Do nothing',
    icon: <DoNotDisturbIcon color='primary' />,
    isValidChildFor: () => true,
    createNode: () => ({}) as any
  }
];

interface NodePaletteProps {
  onExpandedChange?: (expanded: boolean) => void;
  isLocked?: boolean;
}

function NodePalette({
  onExpandedChange,
  isLocked = false
}: Readonly<NodePaletteProps>): React.ReactElement | null {
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleExpanded = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    onExpandedChange?.(newExpanded);
  };

  // Hide the palette entirely when the question is locked
  if (isLocked) {
    return null;
  }

  return (
    <Box sx={styles.paletteContainer(isExpanded)}>
      <Box sx={styles.toggleTab} onClick={toggleExpanded}>
        <IconButton size='small' sx={styles.toggleButton}>
          {isExpanded ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
        {isExpanded ? (
          <Typography sx={styles.tabTextHorizontal}>COLLAPSE</Typography>
        ) : (
          <Typography sx={styles.tabTextVertical}>NODES</Typography>
        )}
      </Box>

      <Box sx={styles.paletteContent}>
        {isExpanded && (
          <Box sx={styles.nodeListContainer(isExpanded)}>
            <Box sx={styles.nodeList}>
              {nodeOptions.map((option) => (
                <DraggableNode key={option.type} nodeOption={option} />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default NodePalette;
